import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Copy,
  Download,
  Send,
  MessageSquare,
  Sparkles,
  RefreshCw,
  Loader2,
  History,
  RotateCcw,
  Check,
  FileDown,
  Wand2,
} from "lucide-react";
import { motion } from "framer-motion";
import AutobiographyEditor from "@/components/AutobiographyEditor";
import BackToDashboardButton from "@/components/BackToDashboardButton";
import { useAutoGrowTextarea } from "@/hooks/useAutoGrowTextarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PreviewScreen = ({
  autobiography,
  chatHistory,
  onChatMessage,
  onRegenerateWithContext,
  onDirectAdjust,
  onCopy,
  onBackToDashboard,
  onSaveEdit,
  onDownloadPdf,
  isAdjusting,
  versions = [],
  currentVersion = 1,
  onRestoreVersion,
  promptVariants = [],
  selectedPromptVariantId,
  onPromptVariantChange,
}) => {
  const variantById = React.useMemo(() => {
    const map = {};
    promptVariants.forEach((v) => { map[v.id] = v; });
    return map;
  }, [promptVariants]);
  const variantLabel = (id) => variantById[id]?.nom || id;
  const [chatInput, setChatInput] = useState("");
  const chatInputRef = useAutoGrowTextarea(chatInput, { maxHeight: "40vh" });
  const chatEndRef = useRef(null);
  const [showVersions, setShowVersions] = useState(false);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  const handleSendMessage = () => {
    if (!chatInput.trim() || isAdjusting) return;
    onChatMessage(chatInput);
    setChatInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Check if there are pending messages in chat (user messages without regeneration)
  const hasPendingChanges = chatHistory.some(msg => msg.role === "user");

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold">Autobiographe Pro</h1>
              <p className="text-sm text-muted-foreground">Aperçu et ajustements</p>
            </div>
          </div>
          <BackToDashboardButton onClick={onBackToDashboard} />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="space-y-6">
          {/* Preview panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="border-border">
              <CardHeader className="pb-0">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <CardTitle className="font-heading text-2xl" data-testid="preview-title">
                      Votre Mini Autobiographie
                    </CardTitle>
                    {versions.length > 0 && (
                      <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                        Version {currentVersion}/{versions.length}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {versions.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowVersions(!showVersions)}
                        className="gap-2"
                        data-testid="versions-button"
                      >
                        <History className="w-4 h-4" />
                        Historique
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onDownloadPdf}
                      className="gap-2"
                    >
                      <FileDown className="w-4 h-4" />
                      Télécharger en PDF
                    </Button>
                    <Button
                      size="sm"
                      onClick={onCopy}
                      className="gap-2"
                      data-testid="copy-clipboard-button"
                    >
                      <Copy className="w-4 h-4" />
                      Copier le texte
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {/* Version history panel */}
              {showVersions && versions.length > 1 && (
                <div className="mx-6 mt-4 p-4 bg-muted/50 rounded-lg border border-border">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Historique des versions
                  </h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {versions.map((v) => (
                      <div
                        key={v.version}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          v.version === currentVersion
                            ? "bg-primary/10 border-primary"
                            : "bg-card border-border hover:bg-muted/50"
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">Version {v.version}</span>
                            {v.version === currentVersion && (
                              <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                                Actuelle
                              </span>
                            )}
                            {v.prompt_variant_id && (
                              <Badge variant="outline" className="text-[10px] font-normal gap-1">
                                <Wand2 className="w-2.5 h-2.5" />
                                {variantLabel(v.prompt_variant_id)}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {v.modification}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(v.timestamp).toLocaleString('fr-FR')}
                          </p>
                        </div>
                        {v.version !== currentVersion && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRestoreVersion(v.version)}
                            className="ml-2 gap-1"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Restaurer
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <CardContent className="pt-4">
                <ScrollArea className="h-[500px] pr-4">
                  <article
                    className="prose-autobiography autobiography-content"
                    data-testid="preview-content"
                  >
                    <AutobiographyEditor
                      autobiography={autobiography}
                      onSave={onSaveEdit}
                    />
                  </article>
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>

          {/* Chat panel - below */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Affiner l'autobiographie
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    {promptVariants.length > 1 && (
                      <Select
                        value={selectedPromptVariantId || ""}
                        onValueChange={onPromptVariantChange}
                      >
                        <SelectTrigger className="w-[240px] h-9 text-sm" data-testid="variant-selector">
                          <div className="flex items-center gap-2 truncate">
                            <Wand2 className="w-3.5 h-3.5 text-primary shrink-0" />
                            <SelectValue placeholder="Style de rédaction" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {promptVariants.map((v) => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.nom}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {hasPendingChanges && (
                      <Button
                        onClick={onRegenerateWithContext}
                        disabled={isAdjusting}
                        className="gap-2"
                        data-testid="regenerate-button"
                      >
                        {isAdjusting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Régénérer avec mes retours
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Discutez avec l'IA pour préciser vos demandes, puis cliquez sur "Régénérer" pour appliquer les modifications. Vous pouvez aussi changer le style de rédaction avant de régénérer.
                </p>
              </CardHeader>
              <CardContent>
                {/* Chat history - show first if there are messages */}
                {chatHistory.length > 0 && (
                  <div className="mb-4 border border-border rounded-lg p-4 bg-muted/30 max-h-[300px] overflow-y-auto">
                    <div className="space-y-3">
                      {chatHistory.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex ${
                            msg.role === "user" ? "justify-end" : "justify-start"
                          }`}
                          data-testid={`chat-message-${index}`}
                        >
                          <div
                            className={`max-w-[85%] px-4 py-2 text-sm whitespace-pre-wrap ${
                              msg.role === "user"
                                ? "chat-bubble-user"
                                : "chat-bubble-assistant"
                            }`}
                          >
                            {msg.text}
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </div>
                  </div>
                )}

                {/* Input area */}
                <div className="flex gap-3">
                  <Textarea
                    ref={chatInputRef}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Décrivez vos retours, posez des questions, donnez des précisions..."
                    className="resize-none min-h-[80px] max-h-[40vh] overflow-y-auto flex-1"
                    data-testid="chat-input"
                    disabled={isAdjusting}
                  />
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={handleSendMessage}
                      disabled={!chatInput.trim() || isAdjusting}
                      variant="outline"
                      className="gap-2 h-auto flex-1"
                      data-testid="chat-send-button"
                    >
                      {isAdjusting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Envoyer
                    </Button>
                  </div>
                </div>

                {/* Suggestions */}
                <div className="mt-4">
                  <p className="text-xs text-muted-foreground mb-2">Exemples de retours :</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Raccourcis l'histoire 1",
                      "Plus de détails sensoriels",
                      "Ton plus chaleureux",
                      "Peux-tu me poser des questions pour améliorer ?",
                    ].map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => setChatInput(suggestion)}
                        className="text-xs px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
                        disabled={isAdjusting}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default PreviewScreen;
